import { Box, Icon, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { TbChevronDown } from 'react-icons/tb';
import { SettingsInlineEdit } from './primitives';
import { LocationsPicker, selectableLocations } from './UsersInlineCells';
import type { LocationEntry } from '../../hooks/useGetLocations';
import type { OrgVendor } from '../../hooks/useGetSettings';
import {
    isSameLocationList,
    resolveLocationList,
    toLocationIds,
    toLocationList,
    type LocationList,
} from './locationListValue';

/** Vendors store their preference locations in the shared location shape. */
export type VendorLocations = LocationList;

export { toLocationIds };
export const toVendorLocations = toLocationList;
export const isSameVendorLocations = isSameLocationList;
export const resolveVendorLocations = resolveLocationList;

/** Compact cell label: the five preference columns are narrow. */
export const preferenceLabel = (value: VendorLocations): string => {
    if (value === 'all') return 'All';
    if (value.length === 0) return '—';
    return String(value.length);
};

/** Native tooltip listing the shops behind a count. */
export const preferenceTitle = (value: VendorLocations): string | undefined => {
    if (value === 'all') return 'All Locations';
    if (value.length === 0) return undefined;
    return value.map((location) => location.title).join(', ');
};

/**
 * Compact cell label for a vendor's makes. Only dealers scope their terms by
 * make, but everyone else really does cover every make, so the stored 'all'
 * reads the same either way.
 */
export const makesLabel = (makes: OrgVendor['makes']): string => {
    // Older vendors predate the field, and an absent scope is every make.
    if (!makes || makes === 'all') return 'All';
    if (makes.length === 0) return '—';
    if (makes.length === 1) return makes[0].name;
    return `${makes.length} makes`;
};

/** Native tooltip listing the makes behind a count. */
export const makesTitle = (makes: OrgVendor['makes']): string | undefined => {
    if (!makes || makes === 'all') return 'All Makes';
    if (makes.length < 2) return undefined;
    return makes.map((make) => make.name).join(', ');
};

/** The five preference fields on a vendor, in escalating-to-negative order. */
export const PREFERENCE_FIELDS = [
    'requiredLocations',
    'superPreferredLocations',
    'preferredLocations',
    'avoidLocations',
    'prohibitedLocations',
] as const;

export type VendorPreferenceField = (typeof PREFERENCE_FIELDS)[number];

/**
 * Set one preference for a vendor. A location can only hold one preference at
 * a time — the same rule the vendor page's preference matrix enforces — so the
 * shops picked here are dropped from the other four. A category that was
 * org-wide becomes the explicit list of everything it keeps.
 */
export const applyVendorPreference = (
    vendor: OrgVendor,
    field: VendorPreferenceField,
    next: VendorLocations,
    locations: LocationEntry[],
): Partial<OrgVendor> => {
    const changes: Partial<Record<VendorPreferenceField, VendorLocations>> = { [field]: next };
    const shops = selectableLocations(locations);
    const claimed = new Set(
        next === 'all' ? shops.map((shop) => shop._id as string) : next.map((entry) => entry._id),
    );
    // Nothing was claimed, so nothing can be taken from the other categories.
    if (claimed.size === 0) return changes as Partial<OrgVendor>;

    PREFERENCE_FIELDS.forEach((other) => {
        if (other === field) return;
        // Resolved, so a stale id in another column cannot hide a shop that is
        // being claimed here from the exclusivity check.
        const current = resolveLocationList(vendor[other], locations);
        if (current === 'all') {
            changes[other] = shops
                .filter((shop) => !claimed.has(shop._id as string))
                .map((shop) => ({ _id: shop._id as string, title: shop.title }));
            return;
        }
        const kept = current.filter((entry) => !claimed.has(entry._id));
        if (kept.length !== current.length) changes[other] = kept;
    });

    return changes as Partial<OrgVendor>;
};

export interface InlineVendorPreferenceCellProps {
    value: VendorLocations;
    locations: LocationEntry[];
    ariaLabel: string;
    onSave: (next: VendorLocations) => void;
    isDisabled?: boolean;
}

/**
 * One preference column for one vendor: how many shops carry that preference,
 * opening a checkbox list of the org's locations. Saves on close, and unlike a
 * user's locations an empty set is a valid value — it just means the vendor
 * holds no such preference anywhere.
 */
export const InlineVendorPreferenceCell = ({
    value,
    locations,
    ariaLabel,
    onSave,
    isDisabled = false,
}: InlineVendorPreferenceCellProps) => {
    // The count and the checkboxes have to come from the same place, or a stored
    // location the org no longer has shows up in the total but never ticks a box.
    const resolved = useMemo(() => resolveVendorLocations(value, locations), [value, locations]);
    const [draft, setDraft] = useState<Array<string> | 'all'>(() => toLocationIds(resolved));
    const isSet = resolved === 'all' || resolved.length > 0;

    const label = (
        <Text fontSize="sm" color={isSet ? 'gray.700' : 'gray.400'} textAlign="center">
            {preferenceLabel(resolved)}
        </Text>
    );

    if (isDisabled) {
        return <Box title={preferenceTitle(resolved)}>{label}</Box>;
    }

    return (
        <SettingsInlineEdit
            display={
                // The count stays centered in the column, so the hover chevron
                // hangs off the right edge instead of pushing it over.
                <Box position="relative" w="100%" title={preferenceTitle(resolved)}>
                    {label}
                    <Icon
                        as={TbChevronDown as React.ElementType}
                        position="absolute"
                        top="50%"
                        right={0}
                        transform="translateY(-50%)"
                        boxSize={3.5}
                        color="gray.400"
                        opacity={0}
                        transition="opacity 0.15s"
                        _groupHover={{ opacity: 1 }}
                    />
                </Box>
            }
            ariaLabel={ariaLabel}
            minW="260px"
            onOpen={() => setDraft(toLocationIds(resolved))}
            onClose={() => {
                const next = toVendorLocations(draft, locations);
                if (!isSameVendorLocations(next, resolved)) onSave(next);
            }}
        >
            <Box>
                <LocationsPicker
                    value={draft}
                    locations={locations}
                    onChange={setDraft}
                    maxH="220px"
                />
                <Text fontSize="xs" color="gray.500" pt={2}>
                    A location holds one preference at a time, so picking it here clears it
                    from the other columns.
                </Text>
            </Box>
        </SettingsInlineEdit>
    );
};
